import React, { useEffect, useRef, useState } from 'react';
import { ReviewTemplateProps } from '../types';
import { TEMPLATE_REGISTRY, getTemplateComponent, TemplateId } from '../TemplateRegistry';
import { TemplateSelectorProps } from '../../type';

const TemplateThumbnail: React.FC<{
    templateId: TemplateId;
    templateProps: ReviewTemplateProps;
    isSelected: boolean;
    onClick: () => void;
}> = React.memo(({ templateId, templateProps, isSelected, onClick }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0.1);

    useEffect(() => {
        const calculateScale = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                const newScale = containerWidth / 1080;
                setScale(newScale);
            }
        };

        calculateScale();
        window.addEventListener('resize', calculateScale);

        return () => window.removeEventListener('resize', calculateScale);
    }, []);

    const TemplateComponent = getTemplateComponent(templateId);

    if (!TemplateComponent) {
        return null;
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className={`relative w-full rounded-xs overflow-hidden shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10)] shadow-md transition-all hover:scale-105 bg-gray-50 ${
                isSelected ? 'ring-2 ring-[#bed000] ring-offset-1' : ''
            }`}
            style={{ aspectRatio: '1/1' }}
        >
            <div
                ref={containerRef}
                className="absolute inset-0 flex items-center justify-center overflow-hidden"
            >
                <div
                    style={{
                        width: '1080px',
                        height: '1080px',
                        transform: `scale(${scale})`,
                        transformOrigin: 'center center',
                        pointerEvents: 'none',
                        transition: 'transform 0.2s ease-out',
                    }}
                >
                    <TemplateComponent {...templateProps} />
                </div>
            </div>
        </button>
    );
});

TemplateThumbnail.displayName = 'TemplateThumbnail';

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
    selectedTemplate,
    onTemplateChange,
    templateProps,
}) => {
    return (
        <div className="self-stretch flex flex-col justify-start items-start gap-2">
            <div className="self-stretch justify-center text-[#393939] text-base font-semibold font-['Montserrat'] leading-tight">
                Choix du template
            </div>
            <div className="self-stretch relative grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-[clamp(0.5rem,1.5vw,1rem)]">
                {TEMPLATE_REGISTRY.map(template => (
                    <TemplateThumbnail
                        key={template.id}
                        templateId={template.id as TemplateId}
                        templateProps={templateProps}
                        isSelected={selectedTemplate === template.id}
                        onClick={() => onTemplateChange(template.id as TemplateId)}
                    />
                ))}
            </div>
        </div>
    );
};

export default TemplateSelector;
